/**
 * Хелперы разметки: единственный способ создавать элементы интерфейса.
 * Все экраны (включая будущие) собираются из этих функций, поэтому классы и
 * структура разметки не «расползаются» по экранам (AGENTS.md: никакого
 * дублирования). Текст ставится только через textContent — innerHTML не используем.
 */
import { createIcon } from './icons';
import type { IconName } from './icons';
import { LOGIN_HREF, navigate } from './router';
import { CONSENT_DOCUMENTS } from '../shared/consents';

/** Значение атрибута: false и null означают «атрибут не ставится», true — пустой атрибут. */
type AttributeValue = string | number | boolean | null;

/** Параметры создания элемента для el(). */
export interface ElOptions {
  /** Классы элемента (БЭМ-подобные имена из styles/components.css). */
  className?: string;
  /** Текстовое содержимое элемента. */
  text?: string;
  /** Атрибуты: id, name, type, autocomplete, aria-*, disabled и т. п. */
  attrs?: Record<string, AttributeValue>;
  /** Обработчики событий, например { click: () => ... }. */
  on?: Record<string, EventListener>;
  /** Дочерние узлы; null пропускается — удобно для условной разметки. */
  children?: (Node | null)[];
}

/** Создаёт элемент с классами, атрибутами, обработчиками и дочерними узлами. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);

  if (options.className) element.className = options.className;
  if (options.text !== undefined) element.textContent = options.text;

  for (const [name, value] of Object.entries(options.attrs ?? {})) {
    if (value === false || value === null) continue;
    element.setAttribute(name, value === true ? '' : String(value));
  }

  for (const [event, listener] of Object.entries(options.on ?? {})) {
    element.addEventListener(event, listener);
  }

  for (const child of options.children ?? []) {
    if (child) element.append(child);
  }

  return element;
}

/** Контрол, который может жить внутри .field. */
export type FieldControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Параметры поля формы для field(). */
export interface FieldOptions {
  /** id контрола: по нему <label for> связывает подпись с полем. */
  id: string;
  /** Текст подписи. */
  label: string;
  /** Контрол: поле само проставит ему id и aria-атрибуты. */
  control: FieldControl;
  /** Подсказка под полем (необязательно). */
  hint?: string;
}

/** Поле формы: корневой узел для вставки в форму и управление сообщением об ошибке. */
export interface FormField {
  /** Корневой элемент .field. */
  root: HTMLDivElement;
  /** Показывает ошибку: .field--invalid + aria-invalid + текст рядом с полем. */
  setError(message: string): void;
  /** Убирает ошибку. */
  clearError(): void;
}

/**
 * Определяет модификатор .field по типу контрола — единственное место, где вид
 * поля зависит от его типа (select, дата, время, файл, чекбокс).
 */
function fieldModifier(control: FieldControl): string {
  if (control instanceof HTMLSelectElement) return 'field--select';
  if (!(control instanceof HTMLInputElement)) return '';

  const modifiers: Record<string, string> = {
    date: 'field--date',
    time: 'field--time',
    file: 'field--file',
    checkbox: 'field--checkbox',
  };
  return modifiers[control.type] ?? '';
}

/**
 * Собирает управление полем вокруг готовой разметки `.field`: место под сообщение об
 * ошибке, её показ и связь с полем через aria-describedby. Общая часть `field()` и
 * `checkboxField()` — сама разметка у них разная.
 */
function formField(root: HTMLDivElement, control: FieldControl, hintId: string | null): FormField {
  const errorId = `${control.id}-error`;
  const errorNode = el('p', { className: 'field__error', attrs: { id: errorId, hidden: true } });
  root.append(errorNode);

  /** Связывает поле с подсказкой и текстом ошибки — для скринридеров. */
  const describe = (): void => {
    const ids = [hintId, errorNode.hidden ? null : errorId].filter(
      (value): value is string => value !== null,
    );
    if (ids.length > 0) control.setAttribute('aria-describedby', ids.join(' '));
    else control.removeAttribute('aria-describedby');
  };
  describe();

  return {
    root,
    setError(message: string): void {
      root.classList.add('field--invalid');
      errorNode.hidden = false;
      errorNode.textContent = message;
      control.setAttribute('aria-invalid', 'true');
      describe();
    },
    clearError(): void {
      root.classList.remove('field--invalid');
      errorNode.hidden = true;
      errorNode.textContent = '';
      control.removeAttribute('aria-invalid');
      describe();
    },
  };
}

/**
 * Создаёт поле формы: <div class="field"> с подписью, контролом, подсказкой и
 * местом под сообщение об ошибке. Ошибка включается классом (.field--invalid),
 * а не инлайн-стилями, и связана с полем через aria-describedby.
 */
export function field(options: FieldOptions): FormField {
  const { id, label, control, hint } = options;
  control.id = id;

  const hintId = hint ? `${id}-hint` : null;
  const root = el('div', {
    className: ['field', fieldModifier(control)].filter(Boolean).join(' '),
    children: [
      el('label', { className: 'field__label', text: label, attrs: { for: id } }),
      control,
      hint ? el('p', { className: 'field__hint', text: hint, attrs: { id: hintId } }) : null,
    ],
  });

  return formField(root, control, hintId);
}

/** Параметры поля-чекбокса для checkboxField(). */
export interface CheckboxFieldOptions {
  /** id контрола: по нему подпись связана с флажком. */
  id: string;
  /** Флажок; id проставляет само поле. */
  control: HTMLInputElement;
  /** Части подписи по порядку: строка — текст, узел — ссылка (см. externalLink). */
  label: (string | Node)[];
}

/**
 * Создаёт поле-чекбокс: флажок слева, подпись справа. Отдельно от `field()`, потому
 * что раскладка другая (флажок стоит первым, подпись — обычным текстом, а не
 * надписью над полем), а подпись собирается из частей: юридические документы в форме
 * регистрации подписаны ссылками на свои страницы (задача 18).
 */
export function checkboxField(options: CheckboxFieldOptions): FormField {
  const { id, control, label } = options;
  control.id = id;

  const root = el('div', {
    className: 'field field--checkbox',
    children: [
      control,
      el('label', {
        className: 'field__label',
        attrs: { for: id },
        children: label.map((part) =>
          typeof part === 'string' ? document.createTextNode(part) : part,
        ),
      }),
    ],
  });

  return formField(root, control, null);
}

/** Визуальные варианты кнопки. */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

/** Параметры кнопки для button(). */
export interface ButtonOptions {
  /** Текст кнопки. */
  text: string;
  /** Вариант оформления; по умолчанию secondary. */
  variant?: ButtonVariant;
  /** Тип кнопки: submit — отправка формы, button — остальные действия. */
  type?: 'button' | 'submit';
  /** Обработчик клика (для type="submit" обычно не нужен). */
  onClick?: () => void;
}

/** Создаёт кнопку .btn с выбранным вариантом и типом. */
export function button(options: ButtonOptions): HTMLButtonElement {
  const { text, variant = 'secondary', type = 'button', onClick } = options;
  return el('button', {
    className: `btn btn--${variant}`,
    text,
    attrs: { type },
    on: onClick ? { click: onClick } : undefined,
  });
}

/** Параметры кнопки-иконки для iconButton(). */
export interface IconButtonOptions {
  /** Имя иконки из реестра icons.ts. */
  icon: IconName;
  /** Подпись для скринридера и всплывающей подсказки, например «Удалить строку». */
  title: string;
  /** Опасное действие (удаление) — красный вариант кнопки. */
  variant?: 'default' | 'danger';
  /** Обработчик клика. */
  onClick?: () => void;
}

/** Создаёт кнопку-иконку .icon-btn с инлайновым SVG внутри. */
export function iconButton(options: IconButtonOptions): HTMLButtonElement {
  const { icon, title, variant = 'default', onClick } = options;
  const element = el('button', {
    className: variant === 'danger' ? 'icon-btn icon-btn--danger' : 'icon-btn',
    attrs: { type: 'button', title, 'aria-label': title },
    on: onClick ? { click: onClick } : undefined,
  });
  element.append(createIcon(icon));
  return element;
}

/** Виды сообщений формы. */
export type MessageKind = 'info' | 'success' | 'error';

/** Блок сообщений: пустой по умолчанию, объявляет результат через aria-live. */
export interface MessageBox {
  /** Корневой элемент .message. */
  root: HTMLDivElement;
  /** Показывает сообщение выбранного вида. */
  show(text: string, kind: MessageKind): void;
  /** Очищает блок. */
  clear(): void;
}

/** Приставка к тексту ошибки — сохраняем прежние формулировки сообщений. */
const ERROR_PREFIX = 'Ошибка: ';

/** Создаёт блок сообщений (aria-live="polite"), слышимый скринридеру. */
export function messageBox(): MessageBox {
  const root = el('div', {
    className: 'message',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });

  return {
    root,
    show(text: string, kind: MessageKind): void {
      root.className = `message message--${kind}`;
      root.textContent = kind === 'error' ? `${ERROR_PREFIX}${text}` : text;
    },
    clear(): void {
      root.className = 'message';
      root.textContent = '';
    },
  };
}

/** Ссылка-переход между экранами SPA (адреса вида #/login). */
export function link(text: string, href: string): HTMLAnchorElement {
  return el('a', {
    className: 'link',
    text,
    attrs: { href },
    on: {
      // Клик обрабатываем сами: к моменту перехода адрес может уже совпадать с
      // целевым (после подтверждения email хеш равен #/login), и тогда переход по
      // обычной ссылке не изменил бы адрес — браузер не бросил бы hashchange, и
      // экран остался бы прежним. Адрес в href сохранён: Ctrl+click и «открыть в
      // новой вкладке» работают как у обычной ссылки.
      click: (event) => {
        event.preventDefault();
        navigate(href);
      },
    },
  });
}

/**
 * Ссылка на отдельную страницу сервиса (юридический документ, задача 18) — обычный
 * переход браузера, а не смена экрана SPA. Открывается в новой вкладке: незаполненная
 * форма регистрации при этом не теряется.
 */
export function externalLink(text: string, href: string): HTMLAnchorElement {
  return el('a', {
    className: 'link',
    text,
    attrs: { href, target: '_blank', rel: 'noopener noreferrer' },
  });
}

/** Название сервиса в мастхеде — постоянной шапке на всех экранах. */
const BRAND_TITLE = 'Элеонора';

/**
 * Адрес логотипа сервиса (задача 20). Файл копируется в корень сборки
 * (`build:frontend`, загрузчик `--loader:.svg=copy`) и отдаётся статикой, как
 * значок сервиса и бланки согласия; адрес абсолютный — мастхед есть и на
 * страницах `/legal/<slug>`, лежащих в подкаталоге.
 */
const LOGO_SRC = '/eleonora_logo.svg';

/** Пункт выпадающего меню мастхеда. */
export interface MastheadMenuItem {
  /** Текст пункта. */
  label: string;
  /** Действие пункта: меню закрывается до его вызова. */
  onClick: () => void;
  /** Необратимое действие (выход из аккаунта) — красный вариант пункта. */
  danger?: boolean;
}

/**
 * Пользователь в правой части мастхеда. Пункты меню — данные, а не разметка: набор
 * ролей пользователя определяет и подпись, и пункты (задача 22, `src/frontend/accountMenu.ts`).
 */
export interface MastheadUser {
  /** Имя и фамилия для кнопки аккаунта. */
  name: string;
  /** Подпись ролей: «Пользователь» / «Организатор» / «Организатор, Администратор». */
  roleLabel: string;
  /**
   * Домашний экран пользователя — цель названия сервиса в мастхеде: у организатора это
   * экран организатора, у остальных — экран заявки (`homeHref` в `src/frontend/router.ts`).
   */
  homeHref: string;
  /** Пункты меню сверху вниз. */
  items: MastheadMenuItem[];
}

/** id меню в разметке: связывает кнопку аккаунта и меню через aria-controls. */
const MASTHEAD_MENU_ID = 'masthead-menu';

/** Кнопка аккаунта с меню: открытие по клику, закрытие по Esc и по клику вне меню. */
function accountMenu(user: MastheadUser): HTMLElement {
  const button = el('button', {
    className: 'masthead__user',
    attrs: { type: 'button', 'aria-expanded': 'false', 'aria-controls': MASTHEAD_MENU_ID },
    children: [
      el('span', { className: 'masthead__user-name', text: user.name }),
      el('span', { className: 'masthead__chevron', children: [createIcon('chevron-down')] }),
    ],
  });

  const menu = el('div', { className: 'menu', attrs: { id: MASTHEAD_MENU_ID, hidden: true } });

  /** Закрывает меню и снимает подписки документа: «висящих» обработчиков не остаётся. */
  function close(): void {
    menu.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onDocumentPointerDown);
    document.removeEventListener('keydown', onDocumentKeyDown);
  }

  /** Открывает меню и подписывается на закрытие: клик вне меню и Esc. */
  function open(): void {
    menu.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onDocumentPointerDown);
    document.addEventListener('keydown', onDocumentKeyDown);
  }

  /** Клик вне кнопки и меню закрывает меню. */
  function onDocumentPointerDown(event: Event): void {
    const target = event.target as Node;
    if (!button.contains(target) && !menu.contains(target)) close();
  }

  /** Esc закрывает меню и возвращает фокус на кнопку — фокус не теряется. */
  function onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    close();
    button.focus();
  }

  button.addEventListener('click', () => {
    if (menu.hidden) open();
    else close();
  });

  menu.append(
    el('div', { className: 'menu__role', text: user.roleLabel }),
    el('ul', {
      className: 'menu__list',
      children: user.items.map((item) =>
        el('li', {
          children: [
            el('button', {
              className: item.danger ? 'menu__item menu__item--danger' : 'menu__item',
              text: item.label,
              attrs: { type: 'button' },
              on: {
                click: () => {
                  close();
                  item.onClick();
                },
              },
            }),
          ],
        }),
      ),
    }),
  );

  return el('div', { className: 'masthead__account', children: [button, menu] });
}

/**
 * Бренд мастхеда — логотип и название одной ссылкой (задача 20): клик по значку
 * ведёт туда же, куда клик по названию. Логотип — декоративная картинка (`alt=""`):
 * ссылку озвучивает название рядом, дублировать его скринридеру незачем.
 */
function brand(homeHref: string): HTMLAnchorElement {
  return el('a', {
    className: 'masthead__brand',
    attrs: { href: homeHref },
    children: [
      el('img', { className: 'masthead__logo', attrs: { src: LOGO_SRC, alt: '' } }),
      el('span', { text: BRAND_TITLE }),
    ],
  });
}

/**
 * Мастхед — постоянная шапка сервиса: слева название, справа аккаунт пользователя.
 * Без пользователя (регистрация, вход, подтверждение email) правая часть пустая.
 * Название ведёт на домашний экран пользователя (у роли он свой) либо на экран входа.
 */
export function masthead(user?: MastheadUser): HTMLElement {
  return el('header', {
    className: 'masthead',
    children: [
      el('div', {
        className: 'masthead__inner',
        children: [brand(user?.homeHref ?? LOGIN_HREF), user ? accountMenu(user) : null],
      }),
    ],
  });
}

/**
 * Подвал страницы: ссылки на юридические документы сервиса (задача 18). Его рисует
 * `renderScreen`, а не отдельные экраны, поэтому подвал есть на всех экранах, включая
 * будущие. Тексты и адреса берутся из единого списка `src/shared/consents.ts` — того
 * же, из которого собираются страницы документов и проверяются версии на сервере.
 */
export function siteFooter(): HTMLElement {
  return el('footer', {
    className: 'footer',
    children: [
      el('div', {
        className: 'footer__inner',
        children: [
          el('p', { className: 'footer__title', text: 'Документы сервиса' }),
          el('ul', {
            className: 'footer__links',
            children: CONSENT_DOCUMENTS.map((document) =>
              el('li', { children: [externalLink(document.title, document.path)] }),
            ),
          }),
        ],
      }),
    ],
  });
}

/**
 * Отрисовывает экран целиком: постоянный мастхед, заголовок экрана, содержимое и
 * подвал. Заменяет всё, что было в точке монтирования #app (SPA без перезагрузки
 * страницы). Заголовок экрана — единственный <h1> страницы: в мастхеде название
 * сервиса оформлено ссылкой, а не заголовком.
 */
export function renderScreen(title: string, content: HTMLElement, user?: MastheadUser): void {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) return;

  app.replaceChildren(
    masthead(user),
    el('main', { className: 'container', children: [el('h1', { text: title }), content] }),
    siteFooter(),
  );
}

